import { Injectable, inject, signal } from '@angular/core';
import { FirebaseApp, initializeApp, getApp, getApps } from 'firebase/app';
import { deleteInstallations, getInstallations } from 'firebase/installations';
import {
  deleteToken,
  getToken,
  MessagePayload,
  Messaging,
  isSupported,
  getMessaging,
  onMessage,
} from 'firebase/messaging';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ReceivedWebNotification } from '../models/product.models';
import { NotificationsService } from './notifications.service';

type WebPushStatus =
  'NotConfigured' | 'Ready' | 'Enabling' | 'Enabled' | 'Denied' | 'Unsupported' | 'Error';

@Injectable({ providedIn: 'root' })
export class FirebaseWebPushService {
  private readonly notifications = inject(NotificationsService);
  private messaging?: Messaging;
  private serviceWorker?: ServiceWorkerRegistration;
  private unsubscribeForeground?: () => void;

  readonly status = signal<WebPushStatus>('Ready');
  readonly installationId = signal('');
  readonly errorMessage = signal('');
  readonly receivedMessages = signal<ReceivedWebNotification[]>([]);

  constructor() {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      queueMicrotask(() => void this.enable().catch(() => undefined));
    }
  }

  async enable(): Promise<string> {
    if (this.status() === 'Enabling') return this.installationId();
    if (!this.isConfigured()) {
      this.status.set('NotConfigured');
      throw new Error('Firebase web configuration and VAPID key are required in environment.ts.');
    }
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !(await isSupported())) {
      this.status.set('Unsupported');
      throw new Error('This browser does not support Firebase web push notifications.');
    }

    this.status.set('Enabling');
    this.errorMessage.set('');
    try {
      if (Notification.permission === 'denied') {
        this.status.set('Denied');
        throw new Error(
          'Notifications are blocked for this site. Allow them in the browser site settings, then click Enable again.',
        );
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        this.status.set('Denied');
        throw new Error('Browser notification permission was not granted.');
      }

      const serviceWorker = await navigator.serviceWorker.register(this.serviceWorkerUrl(), {
        scope: '/',
      });
      this.serviceWorker = serviceWorker;
      const app = getApps().some((item) => item.name === 'zepbazaar-web-push')
        ? getApp('zepbazaar-web-push')
        : initializeApp(this.firebaseConfig(), 'zepbazaar-web-push');
      this.messaging = getMessaging(app);
      const registrationToken = await this.getRegistrationToken(app, this.messaging, serviceWorker);
      if (!registrationToken) {
        throw new Error('Firebase did not return a browser registration token.');
      }
      await firstValueFrom(this.notifications.registerDevice(registrationToken));

      this.installationId.set(registrationToken);
      this.startForegroundListener(this.messaging);
      this.status.set('Enabled');
      return registrationToken;
    } catch (error) {
      const message = this.registrationErrorMessage(error);
      this.errorMessage.set(message);
      if (this.status() === 'Enabling') this.status.set('Error');
      throw new Error(message, { cause: error });
    }
  }

  private startForegroundListener(messaging: Messaging): void {
    this.unsubscribeForeground?.();
    this.unsubscribeForeground = onMessage(messaging, (payload) => {
      const received = this.toReceivedNotification(payload);
      this.receivedMessages.update((items) => [received, ...items].slice(0, 10));
      void this.showForegroundNotification(received);
    });
  }

  private async showForegroundNotification(notification: ReceivedWebNotification): Promise<void> {
    if (Notification.permission !== 'granted' || !this.serviceWorker) return;

    await this.serviceWorker
      .showNotification(notification.title, {
        body: notification.message,
        icon: '/brand/app-icon-192.png',
        badge: '/brand/favicon-64.png',
        data: notification.data,
        tag: notification.id,
      })
      .catch(() => undefined);
  }

  private async getRegistrationToken(
    app: FirebaseApp,
    messaging: Messaging,
    serviceWorker: ServiceWorkerRegistration,
  ): Promise<string> {
    const options = {
      vapidKey: environment.firebase.vapidKey,
      serviceWorkerRegistration: serviceWorker,
    };

    try {
      return await getToken(messaging, options);
    } catch (error) {
      if (!this.isFirebaseRegistrationCredentialError(error)) throw error;

      // Firebase can retain an invalid FID/auth token after app or registration-flow changes.
      await deleteToken(messaging).catch(() => undefined);
      await serviceWorker.pushManager
        .getSubscription()
        .then((subscription) => subscription?.unsubscribe());
      await deleteInstallations(getInstallations(app)).catch(() => undefined);
      return getToken(messaging, options);
    }
  }

  private toReceivedNotification(payload: MessagePayload): ReceivedWebNotification {
    return {
      id: payload.messageId ?? crypto.randomUUID(),
      title: payload.notification?.title ?? 'Notification',
      message: payload.notification?.body ?? '',
      data: payload.data ?? {},
      receivedAt: new Date(),
    };
  }

  private isConfigured(): boolean {
    const config = environment.firebase;
    return Boolean(
      config.apiKey &&
      config.projectId &&
      config.messagingSenderId &&
      config.appId &&
      config.vapidKey,
    );
  }

  private firebaseConfig() {
    const { vapidKey: _vapidKey, ...config } = environment.firebase;
    return config;
  }

  private serviceWorkerUrl(): string {
    const parameters = new URLSearchParams(this.firebaseConfig());
    return `/firebase-messaging-sw.js?${parameters.toString()}`;
  }

  private registrationErrorMessage(error: unknown): string {
    const message = error instanceof Error ? error.message : String(error ?? '');
    const code = this.firebaseErrorCode(error);
    if (this.isFirebaseRegistrationCredentialError(error)) {
      const detail = code ? ` (${code})` : '';
      return `Firebase registration was rejected${detail}. The cached Firebase installation was reset and retried. Verify that the Web app VAPID key belongs to project "${environment.firebase.projectId}".`;
    }
    return code ? `${message} (${code})` : message || 'Unable to enable Firebase web push.';
  }

  private isFirebaseRegistrationCredentialError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error ?? '');
    const code = this.firebaseErrorCode(error);
    return /authentication credential|api[_ -]?key|permission_denied|forbidden|fid-registration-failed/i.test(
      `${code} ${message}`,
    );
  }

  private firebaseErrorCode(error: unknown): string {
    if (!error || typeof error !== 'object' || !('code' in error)) return '';
    return typeof error.code === 'string' ? error.code : '';
  }
}
